import React, { useCallback, useEffect, useState } from "react";
import {
    Share2, Link as LinkIcon, RefreshCw, Power, X, Eye, Clock,
    Copy, Check, ExternalLink, Loader2, MessageCircle, AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import dict from "../i18n/es-MX";

// Builds the absolute public URL using window.location.origin.
function buildPublicUrl(token) {
    if (!token) return null;
    if (typeof window === "undefined") return `/track/${token}`;
    return `${window.location.origin}/track/${token}`;
}

import { fmtDateTime } from "../lib/dateFormat";

export default function PublicLinkModal({ open, onClose, jobId, jobCode, clientName }) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState(false);
    const [confirmRegen, setConfirmRegen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const r = await api.get(`/jobs/${jobId}/public-link`);
            setState(r.data);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, [jobId]);

    useEffect(() => { if (open) { load(); setConfirmRegen(false); setCopied(false); } }, [open, load]);

    const call = async (action) => {
        setBusy(true); setErr("");
        try {
            const r = await api.post(`/jobs/${jobId}/public-link/${action}`, {});
            setState(r.data);
            setConfirmRegen(false);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const url = state?.enabled && state?.token ? buildPublicUrl(state.token) : null;

    const copyToClipboard = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (e) {
            setErr("No se pudo copiar al portapapeles");
        }
    };

    const whatsappHref = (() => {
        if (!url) return "#";
        const tpl = (dict.publicLink.whatsappTemplate || "")
            .replace("{code}", jobCode || "")
            .replace("{client}", clientName || "")
            .replace("{url}", url);
        return `https://wa.me/?text=${encodeURIComponent(tpl)}`;
    })();

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="public-link-modal"
        >
            <div
                className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
                    <div>
                        <h2 className="font-display text-lg font-bold tracking-tight inline-flex items-center gap-2">
                            <Share2 size={18} className="text-brand-orange"/>
                            {dict.publicLink.sectionTitle}
                        </h2>
                        <p className="text-xs text-slate-400 mt-0.5">{jobCode}</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="public-link-close"
                        className="w-9 h-9 rounded-md hover:bg-slate-800 text-slate-400 hover:text-slate-100 inline-flex items-center justify-center">
                        <X size={18}/>
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {loading && <div className="text-center py-8"><Loader2 className="animate-spin inline text-slate-500"/></div>}

                    {!loading && state && (
                        <>
                            {state.enabled ? (
                                <>
                                    <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/40 px-4 py-3 text-sm text-emerald-200" data-testid="public-link-enabled-notice">
                                        {dict.publicLink.explainer}
                                    </div>

                                    <div className="rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-3" data-testid="public-link-url-box">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">URL</div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                readOnly
                                                value={url || ""}
                                                onFocus={(e) => e.target.select()}
                                                data-testid="public-link-url-input"
                                                className="flex-1 bg-transparent text-sm font-mono text-slate-100 truncate outline-none"
                                            />
                                            <button type="button" onClick={copyToClipboard} data-testid="public-link-copy"
                                                className={`shrink-0 h-9 px-3 rounded-md inline-flex items-center gap-1.5 text-xs font-semibold transition ${copied ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700"}`}>
                                                {copied ? <Check size={13}/> : <Copy size={13}/>}
                                                {copied ? dict.publicLink.copied : dict.publicLink.copy}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <a
                                            href={whatsappHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="public-link-whatsapp"
                                            className="h-11 rounded-lg bg-[#25D366] hover:bg-[#1ebd5b] text-slate-950 text-sm font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition"
                                        >
                                            <MessageCircle size={14}/>
                                            WhatsApp
                                        </a>
                                        <a
                                            href={url || "#"}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="public-link-open"
                                            className="h-11 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 text-sm font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition"
                                        >
                                            <ExternalLink size={14}/>
                                            Abrir
                                        </a>
                                    </div>

                                    <div className="rounded-xl border border-slate-800 bg-slate-950/30 px-4 py-3" data-testid="public-link-stats">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1">
                                            <Eye size={11}/> {dict.publicLink.stats}
                                        </div>
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-display text-3xl font-bold text-brand-orange" data-testid="public-link-view-count">{state.view_count || 0}</span>
                                            <span className="text-xs text-slate-400">{dict.publicLink.viewsLabel}</span>
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                                            <Clock size={10}/>
                                            {dict.publicLink.lastViewedAt}: <span className="text-slate-300">{state.last_viewed_at ? fmtDateTime(state.last_viewed_at) : dict.publicLink.never}</span>
                                        </div>
                                    </div>

                                    {!confirmRegen ? (
                                        <div className="flex gap-2 pt-2 border-t border-slate-800">
                                            <button
                                                type="button"
                                                onClick={() => setConfirmRegen(true)}
                                                disabled={busy}
                                                data-testid="public-link-regenerate"
                                                className="flex-1 h-10 rounded-lg border border-slate-700 hover:border-amber-500/60 hover:bg-amber-500/5 text-amber-300 text-xs font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition"
                                            >
                                                <RefreshCw size={13}/>
                                                {dict.publicLink.regenerate}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => call("disable")}
                                                disabled={busy}
                                                data-testid="public-link-disable"
                                                className="flex-1 h-10 rounded-lg border border-slate-700 hover:border-red-500/60 hover:bg-red-500/5 text-red-300 text-xs font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition"
                                            >
                                                <Power size={13}/>
                                                {dict.publicLink.disable}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-3" data-testid="public-link-regenerate-confirm">
                                            <div className="text-sm text-amber-200 inline-flex items-start gap-2">
                                                <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                                                {dict.publicLink.regenerateConfirm}
                                            </div>
                                            <div className="flex gap-2">
                                                <button type="button" onClick={() => setConfirmRegen(false)} className="flex-1 h-9 rounded-md border border-slate-700 hover:bg-slate-800 text-slate-300 text-xs">
                                                    {dict.common.cancel}
                                                </button>
                                                <button type="button" onClick={() => call("regenerate")} disabled={busy} data-testid="public-link-regenerate-confirm-btn"
                                                    className="flex-1 h-9 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                                                    {busy ? <Loader2 className="animate-spin" size={12}/> : <RefreshCw size={12}/>}
                                                    {dict.publicLink.regenerate}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="rounded-xl border border-slate-800 bg-slate-950/40 px-4 py-6 text-center">
                                        <LinkIcon className="text-slate-500 mx-auto mb-2" size={24}/>
                                        <p className="text-sm text-slate-300">{dict.publicLink.explainer}</p>
                                    </div>
                                    {state.revoked_at && (
                                        <div className="text-xs text-amber-300 text-center" data-testid="public-link-disabled-notice">
                                            {dict.publicLink.disabledNotice}
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => call("enable")}
                                        disabled={busy}
                                        data-testid="public-link-enable"
                                        className="w-full h-12 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition"
                                    >
                                        {busy ? <Loader2 className="animate-spin" size={14}/> : <Share2 size={14}/>}
                                        {dict.publicLink.enable}
                                    </button>
                                </>
                            )}
                        </>
                    )}

                    {err && (
                        <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="public-link-err">
                            {err}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
